import { useEffect, useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { reviews, reviewsAreSample } from '@/mocks/reviews';

gsap.registerPlugin(ScrollTrigger);

function Stars({ rating }: { rating: number }) {
  return (
    <div className="flex items-center gap-0.5 text-[13px] leading-none" aria-label={`${rating} out of 5 stars`}>
      {[1, 2, 3, 4, 5].map((i) => (
        <span key={i} className="w-4 h-4 flex items-center justify-center">
          <i className={i <= rating ? 'ri-star-fill text-accent-500' : 'ri-star-line chapter-muted'}></i>
        </span>
      ))}
    </div>
  );
}

/**
 * 06 Client Perspectives — pinned horizontal review rail (desktop only).
 * Native scroll drives a scrubbed translate; no wheel interception.
 * Mobile / reduced motion: stacked cards.
 */
export default function ClientPerspectivesSection() {
  const outerRef = useRef<HTMLDivElement>(null);
  const sectionRef = useRef<HTMLElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const outer = outerRef.current;
    const track = trackRef.current;
    if (!outer || !track) return undefined;
    const mm = gsap.matchMedia();
    mm.add('(min-width: 1024px) and (prefers-reduced-motion: no-preference)', () => {
      // CSS sticky handles the pin natively (no JS fixed-position swap, so no jump on fast scroll).
      // Outer height = viewport + horizontal travel; the scrubbed tween only moves the track.
      const distance = () => Math.max(0, track.scrollWidth - window.innerWidth);
      const setHeight = () => {
        const next = `${Math.round(window.innerHeight + distance() * 1.15)}px`;
        if (outer.style.height !== next) outer.style.height = next;
      };
      setHeight();
      ScrollTrigger.addEventListener('refreshInit', setHeight);
      const tween = gsap.to(track, {
        x: () => -distance(),
        ease: 'none',
        scrollTrigger: {
          id: 'client-rail',
          refreshPriority: 10,
          trigger: outer,
          start: 'top top',
          end: 'bottom bottom',
          scrub: 0.6,
          invalidateOnRefresh: true,
        },
      });
      ScrollTrigger.refresh();
      return () => {
        ScrollTrigger.removeEventListener('refreshInit', setHeight);
        tween.scrollTrigger?.kill();
        tween.kill();
        outer.style.height = '';
        gsap.set(track, { clearProps: 'transform' });
      };
    });
    return () => mm.revert();
  }, []);

  return (
    <div ref={outerRef} className="relative">
    <section
      ref={sectionRef}
      className="client-rail pt-16 md:pt-20 pb-20 md:pb-28 lg:py-0 lg:sticky lg:top-0 lg:h-screen lg:flex lg:flex-col lg:justify-center overflow-hidden"
      aria-labelledby="client-perspectives-title"
    >
      <div className="wrap">
        <div className="mx-auto max-w-[1280px] grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-12 items-end">
          <div>
            <p className="font-label text-[12px] md:text-[13px] tracking-[0.2em] chapter-muted">CLIENT PERSPECTIVES</p>
            <h2
              id="client-perspectives-title"
              className="mt-5 max-w-[620px] font-display font-normal text-[clamp(2rem,3.4vw,3rem)] leading-[1.1] tracking-[-0.015em] chapter-fg"
            >
              Trust is built through experience.
            </h2>
          </div>
          {reviewsAreSample && (
            <p className="font-label text-[12px] leading-[1.6] chapter-muted lg:text-right">
              Illustrative sample reviews — shown for template purposes only.
            </p>
          )}
        </div>
      </div>

      <div
        ref={trackRef}
        className="mt-12 lg:mt-14 flex flex-col lg:flex-row gap-6 lg:gap-8 px-4 md:px-6 lg:px-[max(1.5rem,calc((100vw-1280px)/2))] lg:w-max will-change-transform"
      >
        {reviews.map((r) => (
          <figure
            key={r.n}
            className="flex flex-col border chapter-border bg-white/[0.04] p-6 md:p-9 lg:p-10 w-full lg:w-[min(60vw,820px)] lg:h-[min(58vh,520px)] shrink-0"
          >
            <div className="flex items-center justify-between pb-5 border-b chapter-border">
              <span className="font-label text-[13px] leading-none tracking-[0.12em] text-accent-400">{r.n}</span>
              <Stars rating={r.rating} />
            </div>
            <div className="flex-1 min-h-0 pt-7">
              <blockquote className="max-w-[660px] font-display font-normal text-[clamp(1.375rem,2vw,1.875rem)] leading-[1.25] tracking-[-0.01em] chapter-fg">
                &ldquo;{r.quote}&rdquo;
              </blockquote>
              <p className="mt-4 max-w-[540px] text-[15px] leading-[1.6] chapter-muted">{r.context}</p>
            </div>
            <figcaption className="mt-6 pt-5 border-t chapter-border flex flex-wrap items-baseline gap-x-4 gap-y-1">
              <span className="text-[15px] chapter-fg">{r.name}</span>
              <span className="font-label text-[13px] chapter-muted">{r.role}</span>
              {r.isSample && (
                <span className="font-label text-[11px] tracking-[0.14em] text-accent-400">SAMPLE</span>
              )}
            </figcaption>
          </figure>
        ))}
      </div>
    </section>
    </div>
  );
}